import * as React from "react";
import * as WebFont from "webfontloader";
import {gsap} from "gsap";

const maxValue = 100;
const minValue = 0;
const startAngle = 0.25*Math.PI;
const endAngle = -0.25*Math.PI;
const deltaAngle = (startAngle - endAngle) / (maxValue - minValue);

const outlineWidth = 1;
const barWidth = 10;

const fontSize = 60;
const fontFamily = '"WG-Stencil D", sans-serif';
const fontColor = "#46463c";

const valueColor = "#46463c";
const potentialValueColor = "#e0e0e0";
const biggerValueColor = "#669933";
const smallerValueColor = "#993333";
const arcColor = "#515148";

const canvasPadding = 10;
const canvasHeight = 145;
const canvasWidth = 145;

const chevronWidth = 10;
const chevronHeight = 5;


export default class SummaryGauge extends React.Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.tween = null;
    }

    componentDidMount() {
        WebFont.load({
            custom: {families: ['WG-Stencil D']},
            active: this.updateCanvas,
            timeout: 1000000
        });
    }

    componentDidUpdate() {
        this.updateCanvas();
    }

    static getAngle = (value) => {
        return startAngle - deltaAngle * value;
    }

    static drawChevron = (ctx, color, chevronPosX, chevronPosY, flipped=false) => {
        ctx.beginPath();
        if (!flipped) {
            ctx.moveTo(chevronPosX, chevronPosY);
            ctx.lineTo(chevronPosX + chevronWidth/2, chevronPosY-chevronHeight);
            ctx.lineTo(chevronPosX + chevronWidth, chevronPosY);
            ctx.lineTo(chevronPosX + chevronWidth/4*3, chevronPosY);
            ctx.lineTo(chevronPosX + chevronWidth/4*2, chevronPosY-chevronHeight/2);
            ctx.lineTo(chevronPosX + chevronWidth/4, chevronPosY);
        } else {
            ctx.moveTo(chevronPosX, chevronPosY-chevronHeight);
            ctx.lineTo(chevronPosX + chevronWidth/2, chevronPosY);
            ctx.lineTo(chevronPosX + chevronWidth, chevronPosY-chevronHeight);
            ctx.lineTo(chevronPosX + chevronWidth/4*3, chevronPosY-chevronHeight);
            ctx.lineTo(chevronPosX + chevronWidth/4*2, chevronPosY-chevronHeight/2);
            ctx.lineTo(chevronPosX + chevronWidth/4, chevronPosY-chevronHeight);
        }
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
    }

    static drawArcs = (ctx, canvasCenterX, canvasCenterY, potentialValue, value, newValue, arcR) => {
        let xStart, xEnd, yStart, yEnd;

        //drawing edge top-line
        xStart = canvasCenterX + (arcR - barWidth) * Math.cos(endAngle);
        yStart = canvasCenterY + (arcR - barWidth) * Math.sin(endAngle);
        xEnd = canvasCenterX + arcR * Math.cos(endAngle);
        yEnd = canvasCenterY + arcR * Math.sin(endAngle);

        ctx.beginPath();
        ctx.moveTo(xStart ,yStart);
        ctx.lineTo(xEnd,yEnd);
        ctx.strokeStyle = arcColor;
        ctx.lineWidth = outlineWidth;
        ctx.stroke();

        //drawing edge bottom-line
        xStart = canvasCenterX + (arcR - barWidth) * Math.cos(startAngle);
        yStart = canvasHeight/2 + (arcR - barWidth) * Math.sin(startAngle);
        xEnd = canvasCenterX + arcR * Math.cos(startAngle);
        yEnd = canvasHeight/2 + arcR * Math.sin(startAngle);

        //drawing edge top-line
        ctx.beginPath();
        ctx.moveTo(xStart ,yStart);
        ctx.lineTo(xEnd,yEnd);
        ctx.strokeStyle = arcColor;
        ctx.lineWidth = outlineWidth;
        ctx.stroke();

        //potential value
        if (potentialValue) {
            ctx.beginPath();
            ctx.arc(canvasCenterX, canvasCenterY, arcR - barWidth/2, startAngle, SummaryGauge.getAngle(potentialValue), true);
            ctx.strokeStyle = potentialValueColor;
            ctx.lineWidth = barWidth;
            ctx.stroke();
        }

        //current value
        ctx.beginPath();
        ctx.arc(canvasCenterX, canvasCenterY, arcR - barWidth/2, startAngle, SummaryGauge.getAngle(value),true);
        ctx.strokeStyle = valueColor;
        ctx.lineWidth = barWidth;
        ctx.stroke();

        //add-on value
        if (newValue && value!== newValue) {
            ctx.beginPath();
            ctx.arc(canvasCenterX, canvasCenterY, arcR - barWidth/2, SummaryGauge.getAngle(value), SummaryGauge.getAngle(newValue), newValue >= value);
            ctx.strokeStyle = (newValue >= value) ? biggerValueColor : smallerValueColor;
            ctx.lineWidth = barWidth;
            ctx.stroke();
        }

        //drawing gauge's arc line edges
        ctx.beginPath();
        ctx.arc(canvasCenterX, canvasCenterY, arcR,startAngle,endAngle,true);
        ctx.strokeStyle = arcColor;
        ctx.lineWidth = outlineWidth;
        ctx.stroke();
    }

    updateCanvas = () => {
        let ctx = this.canvasRef.current.getContext('2d');
        let { value, potentialValue, newValue } = this.props;
        let canvasCenterX = canvasWidth / 2;
        let canvasCenterY = canvasHeight / 2;
        let arcR = canvasWidth / 2 - canvasPadding;
        let chevronPosX =  canvasCenterX + arcR - chevronWidth/2;
        let chevronPosY =  canvasCenterY + arcR * Math.sin(endAngle) + chevronHeight/2;
        let chevronPosSecondY =  chevronPosY - chevronHeight;

        // kills active gauge animation
        if (this.tween) {
            this.tween.kill();
        }
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        SummaryGauge.drawArcs(ctx, canvasCenterX, canvasCenterY, potentialValue, value, newValue, arcR);

        // regular text style
        ctx.fillStyle = fontColor;
        ctx.font = `${fontSize}px` + fontFamily;
        ctx.textAlign = "center";
        ctx.textBaseline = 'middle';

        if (newValue && value!== newValue) {
            var obj = {prop:value};
            var newValueColor = (newValue > value) ? biggerValueColor : smallerValueColor;

            ctx.fillStyle = newValueColor;
            ctx.strokeStyle = newValueColor;
            ctx.lineWidth = barWidth;

            this.tween = gsap.to(obj, 0.5, {
                prop:newValue,
                onUpdate:function() {
                    var tempVal = Math.round(obj.prop);
                    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                    ctx.fillText(tempVal.toString(), canvasCenterX, canvasCenterY);

                    SummaryGauge.drawArcs(ctx, canvasCenterX, canvasCenterY, potentialValue, value, tempVal, arcR);

                    if (newValue > value) {
                        SummaryGauge.drawChevron(ctx, biggerValueColor, chevronPosX, chevronPosY, false);
                        SummaryGauge.drawChevron(ctx, biggerValueColor, chevronPosX, chevronPosSecondY, false);
                    } else if (newValue < value) {
                        SummaryGauge.drawChevron(ctx, smallerValueColor, chevronPosX, chevronPosY, true);
                        SummaryGauge.drawChevron(ctx, smallerValueColor, chevronPosX, chevronPosSecondY, true);
                    }
                }
            });
        } else {
            ctx.fillText(value, canvasCenterX, canvasCenterY);
        }
    }

    render() {
        const { title, label_ico, value, newValue } = this.props;
        const outputValue = newValue  ? newValue : value;
        let outputValueModifier ='';
        if (newValue && value!== newValue) {
            outputValueModifier = (newValue >= value) ? 'tank-summary-gauge__increased' : 'tank-summary-gauge__decreased';
        }
        return(
            <div className={"tank-summary-gauge " + outputValueModifier}>
                <span className="tank-summary-gauge_icon">
                    <span className="svg-icon js-svg-icon"
                          data-src={ STATIC_URL +"tankopedia/img/svg-icons/" + label_ico + "_icon.svg"}>
                    </span>
                </span>
                <span className="tank-summary-gauge_value">
                    {outputValue}
                </span>
                <span className="tank-summary-gauge_title">{title}</span>
                <div className="tank-summary-gauge_body">
                    <canvas
                        ref={this.canvasRef}
                        width={canvasWidth}
                        height={canvasHeight}
                    />
                </div>
            </div>
        );
    }
}